import React, { useState } from "react";
import { Box, Button, Paper, Grid, useTheme, Typography, CircularProgress, Select, MenuItem, Popup } from "@mui/material";
import AxionControls from "../../../components/controls/AxionControls";
import ProductionPlanInfoDialog from "./productionPlanInfoDialog";
import GridDateTimePicker from "../../home/components/GridDateTimePicker";
import messages from "../messages";
import { useMsal, useAccount } from "@azure/msal-react";
import { tokens } from "../../../assets/theme";
import { FormattedMessage, useIntl } from "react-intl";
import { postNewPO } from "../../../fetchers/productionPlan";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import {
    DataGridPro,
    GridLogicOperator,
    useGridApiRef,
    gridFilterModelSelector,
    GridToolbar,
    gridExpandedSortedRowIdsSelector
} from "@mui/x-data-grid-pro";
import { PanoramaVerticalSelectRounded } from "@mui/icons-material";
import { type } from "jquery";
import dayjs from "dayjs";

export default function ProductionPlanMachineList(props) {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);

    const [quantity, setQuantity] = React.useState(0);
    const [startTime, setStartTime] = React.useState(new Date());
    const [productId, setProductId] = React.useState(null);
    const [machineId, setMachineId] = React.useState(props?.machine?.la?.id);
    const [showBackdrop, setShowBackdrop] = React.useState(false);
    const [buttonDisabled, setButtonDisabled] = React.useState(false);
    const [openPopup, setOpenPopup] = React.useState(false);
    const apiRef = useGridApiRef();

    const isoFormat = "YYYY-MM-DDTHH:mm:ss";

    //console.log("ProductionPlanMachineList");
    //console.log("ProductionPlanMachineList Data:", props.plannedData);


    if (props.plannedData[0]?.length === 0) {
        return <Typography variant="h6" gutterBottom>
            No data!!!
        </Typography>;
    }

    function formatDate(date) {
        return date ? new Date(date).toLocaleDateString('sv-SE') : "";
    }
    function formatTime(date) {
        return date ? new Date(date).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : "";
    }
    function formatDateAndTime(date) {
        return date ? formatDate(date) + " " + formatTime(date) : "";
    }

    function isActivePO(pPO, pActivePO) {
        if (pPO !== undefined && pPO !== null && pActivePO !== undefined && pActivePO !== null) {
            if (pPO.toString() === pActivePO.toString()) {
                return true; // This is the active production order
            }
        }
        return false;
    }
    function getPoSymbol(obj) {
        let symbol = "";
        if (isActivePO(obj.productionOrderId, obj.activePO)) {
            //ongoing = String.fromCodePoint(9733);
            //ongoing = String.fromCodePoint(10148) + " ";
            symbol = " " + String.fromCodePoint(9654) + "";  //play
            //ongoing = "▶ ";
            //ongoing = "► ";
            //ongoing = "✔ ";
            //ongoing = "⏸ ";

            //ongoing = String.fromCodePoint(128512) + " ";
        } else {
            switch (obj.erpStatus) {
                case "1":
                    symbol = "P ";
                    break;
                case "7":
                    symbol = " ⏸";
                    break;
                case "9":
                    symbol = " ✔";
                    break;
                default:
                    symbol = " ?";
                    //ongoing = "✔ ";
                    break;
            }
            // Pyramid status
            // 0 – Prognos
            // 1 – Registrerad
            // 2 – Reserverad
            // 3 – Uttag gjorda
            // 4 – Återrapporterad
            // 7 – Vilande
            // 9 – Avslutad
        }
        return symbol;
    }

    function calculateRunTimeMinutes(start, end) {
        if (start && end) {
            let startDate = new Date(start);
            let endDate = new Date(end);
            let diff = endDate - startDate;
            let diffMinutes = Math.floor(diff / 1000 / 60);
            return diffMinutes;
        }
        return 0;
    }

    function splitMinutesIntoHoursAndMinutes(totalMinutes) {
        if (totalMinutes === null || totalMinutes === undefined || totalMinutes === 0) {
            return "";
        }
        if (totalMinutes > 60) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            //return formatted text
            return `${hours}h ${minutes}m`;
        }
        return `${totalMinutes}m`;
    }

    //flatten the data to get rows for DataGridPro
    let data = props.plannedData[0]?.children?.map((machine) => {
        return machine?.periods?.filter((x) => x.type === "main").map((row) => ({
            id: row.id,
            machineId: machine.id,
            machineName: machine.name,
            machineStatus: machine.machineStatus,
            machinePriorityIcon: machine.machinePriorityIcon,
            productionOrderId: row.productionOrderId,
            productionOrderWithSymbol: row.productionOrderId ? row.productionOrderId + getPoSymbol(row) : "",
            itemNo: row.itemNo,
            itemName: row.itemName,
            plannedQty: row.plannedQty,
            plannedStartTime: row.plannedStartTime,
            plannedEndTime: row.plannedEndTime,
            replannedStartTime: row.replannedStartTime,
            replannedEndTime: row.replannedEndTime,
            calculatedStartTime: row.calculatedStartTime,
            calculatedEndTime: row.calculatedEndTime,
            calculatedRunTime: splitMinutesIntoHoursAndMinutes(calculateRunTimeMinutes(row.calculatedStartTime, row.calculatedEndTime)),
            calculatedTimeLeft: splitMinutesIntoHoursAndMinutes(calculateRunTimeMinutes(new Date(), row.calculatedEndTime)),
            plannedOrReplannedStartTime: row.plannedOrReplannedStartTime,
            plannedOrReplannedEndTime: row.plannedOrReplannedEndTime,
            poSymbol: getPoSymbol(row),

            activePO: row.activePO,
            erpStatus: row.erpStatus,

            type: row.type,
            path: [row.id.toString()],
        }))
    })

    let origData = [...data];

    let filteredData = [];
    data.forEach((machine) => {
        let row = machine.filter((row) => isActivePO(row.productionOrderId, row.activePO));
        if (row.length > 0) {

            filteredData.push(row[0]);
        }
    });

    data = filteredData;

    //flatten the array
    data = data ? data.flat() : [];

    origData = origData ? origData.flat() : [];
    //sort by start time
    origData = origData?.sort((a, b) => new Date(a.plannedOrReplannedStartTime) - new Date(b.plannedOrReplannedStartTime));
    //sort by machine name
    origData = origData?.sort((a, b) => a.machineName.localeCompare(b.machineName));


    //console.log("props.machineData:", props.machineData);


    //Add machines missing in current running
    let missingMachines = [];
    let machines = props.machineData?.filter((m) => m.machineTypeId === 1); // only production machines
    machines?.forEach((machine) => {
        let exists = data.find((d) => d.machineId === machine.id);
        if (!exists) {
            missingMachines.push({
                id: parseInt(machine.id.toString()), //machine.id,
                machineId: machine.id,
                machineName: machine.machineName,
                machineStatus: machine.status,
                machinePriorityIcon: machine.priorityIcon,
                productionOrderId: "",
                productionOrderWithSymbol: "",
                itemNo: null,
                itemName: null,
                plannedQty: null,
                plannedStartTime: null,
                plannedEndTime: null,
                replannedStartTime: null,
                replannedEndTime: null,
                calculatedStartTime: null,
                calculatedEndTime: null,
                calculatedRunTime: null,
                calculatedTimeLeft: null,
                plannedOrReplannedStartTime: dayjs().format(isoFormat),
                plannedOrReplannedEndTime: dayjs().format(isoFormat),
                poSymbol: null,
                activePO: null,
                erpStatus: null,
                type: "main",
                path: [parseInt(machine.id.toString())], // set path for tree <data value=""></data>
            });
        }
    });

    data = data.concat(missingMachines);

    //get all rows with plannedorReplannedEndTime directly after each item in data
    let allAdditionalRows = [];
    data.forEach((row) => {
        let additionalRows = origData.filter((r) => r.machineId.toString() === row.machineId.toString() && r.plannedOrReplannedStartTime >= row.plannedOrReplannedStartTime && r.productionOrderId !== row.productionOrderId); // && r.productionOrderId !== row.productionOrderId
        //console.log("additional rows for machineId", row.machineId, ":", additionalRows);
        if (additionalRows !== undefined) {
            additionalRows = additionalRows.map((r) => ({ ...r, path: [row.id.toString(), r.id.toString()] })); // set path for tree data
            allAdditionalRows.push(...additionalRows);
        }
    });

    allAdditionalRows = allAdditionalRows.flat();
    //console.log("all additional rows:", allAdditionalRows);

    data = data.concat(allAdditionalRows);
    data = data.flat();

    //sort by start time
    data = data?.sort((a, b) => new Date(a.plannedOrReplannedStartTime) - new Date(b.plannedOrReplannedStartTime));
    //sort by machine name
    data = data?.sort((a, b) => a.machineName.localeCompare(b.machineName));

    let columns = [  //: GridColDef[]
        {
            field: "machineName",
            headerName: "Machine",
            width: 100,
            headerAlign: "left",
        },
        {
            field: "productionOrderId",
            headerName: "PO",
            width: 80,
            type: "number",
            align: "right",
        },
        {
            field: "poSymbol",
            headerName: "",
            width: 30,
            align: "center",
        },
        {
            field: "itemNo",
            headerName: "Item no",
            width: 120,
            type: "number",
            align: "right",
        },
        {
            field: "itemName",
            headerName: "Item name",
            width: 150,
        },
        {
            field: "plannedQty",
            headerName: "Planned Qty",
            width: 100,
            type: "number",
            align: "right",
        },
        {
            field: "plannedStartTime",
            headerName: "Planned start",
            width: 150,
            renderCell: ({ id, value, field }) => {
                return value ? formatDateAndTime(value) : "";
            },
        },
        {
            field: "plannedEndTime",
            headerName: "Planned end",
            width: 150,
            renderCell: ({ id, value, field }) => {
                return value ? formatDateAndTime(value) : "";
            },
        },
        {
            field: "rePlannedStartTime",
            headerName: "Replanned start",
            width: 150,
            renderCell: ({ id, value, field }) => {
                return value ? formatDateAndTime(value) : "";
            },
        },
        {
            field: "rePlannedEndTime",
            headerName: "Replanned end",
            width: 150,
            renderCell: ({ id, value, field }) => {
                return value ? formatDateAndTime(value) : "";
            },
        },
        {
            field: "calculatedStartTime",
            headerName: "Calculated start",
            width: 150,
            renderCell: ({ id, value, field }) => {
                return value ? formatDateAndTime(value) : "";
            },
        },
        {
            field: "calculatedEndTime",
            headerName: "Calculated end",
            width: 150,
            renderCell: ({ id, value, field }) => {
                return value ? formatDateAndTime(value) : "";
            },
        },
        {
            field: "calculatedRunTime",
            headerName: "Calculated run time",
            width: 150,
            renderCell: ({ id, value, field }) => {
                return value ? value : "";
            },
        },
        {
            field: "calculatedTimeLeft",
            headerName: "Calculated time left",
            width: 150,
            renderCell: ({ id, value, field }) => {
                return value ? value : "";
            },
        },

    ]

    // If your data is a “path”, provide getTreeDataPath directly.
    // If you have parent IDs, convert them to paths or use a custom resolver.
    const getTreeDataPath = (row) => {
        // Example using a denormalized path you attach to each row
        return row.path; // e.g. ["Sarah", "Thomas", "Robert"]
    };

    return (
        <>
            <AxionControls.AxionBackdrop open={showBackdrop} />

            <Box paddingLeft="1px" marginLeft="1px"
                sx={{
                    "& .MuiDataGrid-root": {
                        backgroundColor: colors.primary[400],
                        borderColor: `${colors.primary[700]} !important`,
                        borderRadius: "0px",
                        fontSize: "1.0rem",
                        border: "0 !important",
                    },
                    "& .MuiDataGrid-main": {
                        borderRadius: "0px",
                        border: "0 !important",
                        borderColor: `${colors.primary[700]} !important`,
                    },
                    "& .MuiDataGrid-cell": {
                        borderColor: `${colors.primary[700]} !important`,
                        borderWidth: "2px !important",
                    },
                    "& .name.column--cell": {
                        color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.primary[400],
                        fontSize: '1.0rem',
                        borderColor: `${colors.primary[700]} !important`,
                        borderWidth: "2px"
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.primary[600],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.gray[100]} !important`,
                    },
                    "& .super-app-theme--11": {
                        bgcolor: `${colors.redAccent[500]} !important`,
                    },
                    "& .super-app-theme--undefined-stop": {
                        bgcolor: `${colors.redAccent[500]} !important`,
                    },
                    "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                        opacity: 0,
                        transition: "opacity 0.2s"
                    },
                    "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                        opacity: 1
                    },
                    "& .expanded-parent": {
                        borderLeft: "4px solid red",
                        fontWeight: 600,
                    },
                    "& .child-row": {
                        borderLeft: "4px solid " + colors.primary[500],
                        backgroundColor: "#30333c",
                        fontWeight: 600
                    },
                }}
            >
                <DataGridPro
                    apiRef={apiRef}
                    columns={columns}
                    rows={data}
                    treeData
                    getTreeDataPath={getTreeDataPath}
                    groupingColDef={{ headerName: "Expand rows" }}
                    defaultGroupingExpansionDepth={0} // -1 to expand all
                    onRowClick={(params, event) => {
                        event.defaultMuiPrevented = true;
                        console.log("Row clicked:", params.row);
                        props.handleClickRow(params.row);
                    }}

                    disableRowSelectionOnClick={true}
                    rowSelection={false}

                    getRowClassName={(params) => {
                        const id = params.id;
                        //console.log ("getRowClassName params:", params);  
                        //console.log ("getRowClassName apiRef:", apiRef);
                        //####const node = apiRef?.current?.state?.rowTree?.[id];
                        const node = apiRef?.current?.state?.rows?.tree?.[id];
                        //console.log ("getRowClassName id:", node);
                        const isExpanded = !!node?.childrenExpanded;
                        const pathLength = (params.row.path || []).length;



                        if (pathLength > 1) return "child-row";       // children → green
                        //if (isExpanded) return "expanded-parent";     // expanded parents → red
                        return "parent-row";                          // collapsed parents → default
                    }}


                    components={{
                        Toolbar: GridToolbar,
                    }}
                    slotProps={{
                        // footer: {
                        //     machinesRunning,
                        //     machinesStopped,
                        //     machinesPlannedStop
                        // },
                        panel: {
                            sx: {
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: "#799c4a",
                                    opacity: 0.3,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                    backgroundColor: "#799c4a",
                                },
                                // "& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb": {
                                //     backgroundColor: colors.gray[100],
                                // },
                                "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                                    color: colors.primary[200],
                                    fontSize: "0.75rem",
                                    "&:hover": {
                                        color: colors.primary[300],
                                    },
                                },
                                "& .MuiFormLabel-root.Mui-focused": {
                                    color: colors.primary[200],
                                },
                                /* 🔴 Expanded parent rows */
                                "& .expanded-parent .MuiDataGrid-cell": {
                                    backgroundColor: "#ff00ff"
                                },
                                "& .expanded-parent": {
                                    borderLeft: "4px solid #2fd34aff",
                                    fontWeight: 600
                                },

                                "& .expanded-parent111": {
                                    borderLeft: "4px solid #6e2fd3ff",
                                    fontWeight: 600
                                },

                                /* 🟩 Child rows */
                                "& .child-rowx": {
                                    borderLeft: "4px solid #2fd34aff",
                                    fontWeight: 600
                                },
                                // "& .MuiDataGrid-panel .child-row .MuiDataGrid-row": {
                                //     borderLeft: "4px solid #00ff00",
                                //     backgroundColor: "#00ff00"
                                // },
                                // "& .child-row .MuiDataGrid-cell": {
                                //     borderLeft: "4px solid #00ff00",
                                //     backgroundColor: "#00ff00"
                                // },

                                // /* optional: parents (collapsed) look normal */
                                // "& .parent-row .MuiDataGrid-cell": {
                                //     backgroundColor: "transparent"
                                // }
                            },
                        }
                    }}
                />

            </Box>
        </>
    );
}

